/* eslint-disable @typescript-eslint/no-unused-vars */
import React,{useState,useEffect} from 'react';
import Layout from '../components/layout/Layout';
import api from '../services/api';
import Badge from '../components/common/Badge';
import {Input,Select} from '../components/common/FormField';

const TABS=[
  ['templates','📄 Report Templates'],
  ['organisms','🦠 Organism Master'],
  ['antibiotics','💊 Antibiotic Master'],
  ['units','📐 Unit Master'],
  ['formulas','🧮 Formula Master'],
  ['rules','🔬 Interpretation Rules'],
];

const DEPT_COLORS:Record<string,string>={
  Haematology:'bg-red-100 text-red-700', Biochemistry:'bg-blue-100 text-[#02357A]',
  Microbiology:'bg-green-100 text-green-700', Pathology:'bg-purple-100 text-purple-700',
  Radiology:'bg-indigo-100 text-indigo-700', Cardiology:'bg-pink-100 text-pink-700',
};
const WHO_COLORS:Record<string,string>={ACCESS:'bg-green-100 text-green-700',WATCH:'bg-amber-100 text-amber-700',RESERVE:'bg-red-100 text-red-700'};
const GRAM_BADGE:Record<string,string>={POSITIVE:'bg-violet-100 text-violet-700',NEGATIVE:'bg-pink-100 text-pink-700',ACID_FAST:'bg-orange-100 text-orange-700',NOT_APPLICABLE:'bg-slate-100 text-slate-500'};
const SEV_COLORS:Record<string,string>={NORMAL:'bg-green-100 text-green-700',MILD:'bg-amber-100 text-amber-700',MODERATE:'bg-orange-100 text-orange-700',SEVERE:'bg-red-100 text-red-700',CRITICAL:'bg-red-900 text-white'};

export default function LabReportMasterPage(){
  const [tab,setTab]=useState<string>('templates');
  const [templates,setTemplates]=useState<any[]>([]);
  const [organisms,setOrganisms]=useState<any[]>([]);
  const [antibiotics,setAntibiotics]=useState<any[]>([]);
  const [units,setUnits]=useState<any[]>([]);
  const [formulas,setFormulas]=useState<any[]>([]);
  const [rules,setRules]=useState<any[]>([]);
  const [loading,setLoading]=useState(false);
  const [saving,setSaving]=useState(false);
  const [msg,setMsg]=useState('');
  const [search,setSearch]=useState('');
  const [filterDept,setFilterDept]=useState('');
  const [showForm,setShowForm]=useState(false);
  const [formType,setFormType]=useState('');
  const [formData,setFormData]=useState<any>({});
  const notify=(m:string)=>{setMsg(m);setTimeout(()=>setMsg(''),4000);};

  const loadData=async()=>{
    setLoading(true);
    try{
      if(tab==='templates'){const r=await api.getLabReportTemplates?.();setTemplates(Array.isArray(r?.data)?r.data:[]);}
      else if(tab==='organisms'){const r=await api.getLabOrganisms?.();setOrganisms(Array.isArray(r?.data)?r.data:[]);}
      else if(tab==='antibiotics'){const r=await api.getLabAntibiotics?.();setAntibiotics(Array.isArray(r?.data)?r.data:[]);}
      else if(tab==='units'){const r=await api.getLabUnits?.();setUnits(Array.isArray(r?.data)?r.data:[]);}
      else if(tab==='formulas'){const r=await api.getLabFormulas?.();setFormulas(Array.isArray(r?.data)?r.data:[]);}
      else if(tab==='rules'){const r=await api.getInterpretationRules?.();setRules(Array.isArray(r?.data)?r.data:[]);}
    }catch{}
    setLoading(false);
  };
  useEffect(()=>{loadData();setSearch('');setShowForm(false);},[tab]);

  const saveRecord=async()=>{
    setSaving(true);
    try{
      if(tab==='templates') await api.createLabReportTemplate?.(formData);
      else if(tab==='organisms') await api.createLabOrganism?.(formData);
      else if(tab==='antibiotics') await api.createLabAntibiotic?.(formData);
      else if(tab==='rules') await api.createInterpretationRule?.(formData);
      notify('✅ Saved!');setShowForm(false);setFormData({});await loadData();
    }catch(e:any){notify('❌ '+(e.response?.data?.message||'Error'));}
    setSaving(false);
  };

  const filter=(arr:any[],keys:string[])=>arr.filter(item=>{
    if(!search) return !filterDept||(item.department||item.antibiotic_class||item.unit_type||item.department||'').toLowerCase().includes(filterDept.toLowerCase());
    return keys.some(k=>(item[k]||'').toLowerCase().includes(search.toLowerCase()));
  });

  return(
    <Layout title="Lab Report Master">
      {msg&&<div className={`fixed top-4 right-4 px-4 py-3 text-sm z-50 rounded-xl shadow-xl border ${msg.startsWith('✅')?'bg-green-50 border-green-200 text-green-800':'bg-red-50 border-red-200 text-red-800'}`}>{msg}</div>}

      <div className="flex gap-1 mb-5 bg-slate-100 rounded-xl p-1 overflow-x-auto">
        {TABS.map(([k,l])=>(
          <button key={k} onClick={()=>setTab(k)} className={`px-4 py-2 text-sm rounded-lg font-medium whitespace-nowrap transition-all ${tab===k?'bg-white shadow text-[#02357A]':'text-slate-500 hover:text-slate-700'}`}>{l}</button>
        ))}
      </div>

      <div className="flex gap-3 mb-4 items-center flex-wrap">
        <input className="input flex-1 min-w-48" placeholder={`🔍 Search ${tab}...`} value={search} onChange={e=>setSearch(e.target.value)}/>
        {tab==='templates'&&<select className="input w-40" value={filterDept} onChange={e=>setFilterDept(e.target.value)}><option value="">All Depts</option>{['Haematology','Biochemistry','Microbiology','Pathology','Radiology','Cardiology'].map(d=><option key={d}>{d}</option>)}</select>}
        {['templates','organisms','antibiotics','rules'].includes(tab)&&(
          <button onClick={()=>{setFormData({});setShowForm(true);}} className="btn-primary text-sm">+ Add {tab==='templates'?'Template':tab==='organisms'?'Organism':tab==='antibiotics'?'Antibiotic':'Rule'}</button>
        )}
      </div>

      {/* ══ REPORT TEMPLATES ══ */}
      {tab==='templates'&&(
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {loading?<p className="col-span-3 text-center text-slate-400 py-8">Loading...</p>
          :filter(templates,['template_name','template_code','department','category']).map((t:any)=>(
            <div key={t.id} className="card p-4 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <p className="font-semibold text-slate-800 text-sm">{t.template_name}</p>
                  <p className="text-xs font-mono text-slate-400">{t.template_code}</p>
                </div>
                <Badge label={t.report_type} className="text-xs bg-slate-100 text-slate-600"/>
              </div>
              <div className="flex items-center gap-2 mb-3">
                <Badge label={t.department} className={`text-xs ${DEPT_COLORS[t.department]||'bg-slate-100 text-slate-600'}`}/>
                {t.category&&<span className="text-xs text-slate-400">{t.category}</span>}
              </div>
              <div className="flex gap-3 text-xs text-slate-500">
                {t.show_nabl_logo&&<span className="bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded">NABL</span>}
                {t.show_barcode&&<span>📊 Barcode</span>}
                <span>{t.paper_size} {t.orientation}</span>
              </div>
            </div>
          ))}
          {showForm&&(
            <div className="card p-4 border-2 border-blue-200 bg-blue-50 col-span-full md:col-span-1">
              <h3 className="font-semibold text-blue-800 mb-3">New Report Template</h3>
              <div className="space-y-2">
                <div><label className="label text-xs">Template Code *</label><Input value={formData.template_code||''} onChange={e=>setFormData({...formData,template_code:e.target.value.toUpperCase()})} placeholder="TPL_CBC"/></div>
                <div><label className="label text-xs">Template Name *</label><Input value={formData.template_name||''} onChange={e=>setFormData({...formData,template_name:e.target.value})}/></div>
                <div className="grid grid-cols-2 gap-2">
                  <div><label className="label text-xs">Department</label><select className="input text-sm" value={formData.department||''} onChange={e=>setFormData({...formData,department:e.target.value})}><option value="">Select...</option>{['Haematology','Biochemistry','Microbiology','Pathology','Radiology','Cardiology'].map(d=><option key={d}>{d}</option>)}</select></div>
                  <div><label className="label text-xs">Report Type</label><select className="input text-sm" value={formData.report_type||'NORMAL'} onChange={e=>setFormData({...formData,report_type:e.target.value})}>{['NORMAL','CULTURE','RADIOLOGY','HISTOPATHOLOGY','ECG','CYTOLOGY'].map(t=><option key={t}>{t}</option>)}</select></div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div><label className="label text-xs">Paper Size</label><select className="input text-sm" value={formData.paper_size||'A4'} onChange={e=>setFormData({...formData,paper_size:e.target.value})}>{['A4','A5','LETTER'].map(s=><option key={s}>{s}</option>)}</select></div>
                  <div><label className="label text-xs">Orientation</label><select className="input text-sm" value={formData.orientation||'PORTRAIT'} onChange={e=>setFormData({...formData,orientation:e.target.value})}>{['PORTRAIT','LANDSCAPE'].map(o=><option key={o}>{o}</option>)}</select></div>
                </div>
                <div>
                  <label className="label text-xs">Header Color</label>
                  <div className="flex gap-2 items-center">
                    <input type="color" value={formData.header_color||'#1e3a5f'} onChange={e=>setFormData({...formData,header_color:e.target.value})} className="h-9 w-12 rounded border border-slate-200 cursor-pointer"/>
                    <Input value={formData.header_color||'#1e3a5f'} onChange={e=>setFormData({...formData,header_color:e.target.value})} placeholder="#1e3a5f"/>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-x-2 gap-y-1.5 pt-1">
                  {[['show_logo','Hospital Logo'],['show_nabl_logo','NABL Badge'],['show_barcode','Barcode'],['show_qr','QR Code'],['show_watermark','Watermark']].map(([k,l])=>(
                    <label key={k} className="flex items-center gap-1.5 text-xs text-slate-600"><input type="checkbox" checked={formData[k]!==false&&formData[k]!==undefined?!!formData[k]:(k==='show_logo'||k==='show_barcode'||k==='show_watermark')} onChange={e=>setFormData({...formData,[k]:e.target.checked})} className="w-3.5 h-3.5 accent-blue-600"/>{l}</label>
                  ))}
                </div>
                {formData.show_watermark!==false&&<div><label className="label text-xs">Watermark Text</label><Input value={formData.watermark_text||''} onChange={e=>setFormData({...formData,watermark_text:e.target.value})} placeholder="Defaults to hospital name"/></div>}
                <div><label className="label text-xs">Footer Note</label><Input value={formData.footer_text||''} onChange={e=>setFormData({...formData,footer_text:e.target.value})} placeholder="Defaults to standard disclaimer"/></div>
                <div className="flex gap-2 pt-2">
                  <button onClick={saveRecord} disabled={saving} className="btn-primary text-xs flex-1">{saving?'Saving...':'Save'}</button>
                  <button onClick={()=>setShowForm(false)} className="btn-secondary text-xs">Cancel</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ══ ORGANISM MASTER ══ */}
      {tab==='organisms'&&(
        <div className="card overflow-hidden">
          <div className="grid grid-cols-12 p-3 bg-slate-50 border-b text-xs font-semibold text-slate-500 uppercase gap-2">
            <span className="col-span-1">Code</span><span className="col-span-3">Organism Name</span><span className="col-span-2">Gram Stain</span><span className="col-span-2">Class</span><span className="col-span-2">Morphology</span><span className="col-span-1">Common</span><span className="col-span-1">SNOMED</span>
          </div>
          {loading?<p className="p-8 text-center text-slate-400">Loading...</p>
          :<div className="divide-y max-h-[60vh] overflow-y-auto">
            {filter(organisms,['organism_code','organism_name','organism_class','morphology']).map((o:any)=>(
              <div key={o.id} className="grid grid-cols-12 gap-2 p-3 items-center hover:bg-slate-50 text-sm">
                <span className="col-span-1 font-mono text-xs font-bold text-[#02357A]">{o.organism_code}</span>
                <span className="col-span-3 font-medium text-slate-800 italic">{o.organism_name}</span>
                <span className="col-span-2"><Badge label={o.gram_stain||'—'} className={`text-xs ${GRAM_BADGE[o.gram_stain]||'bg-slate-100 text-slate-500'}`}/></span>
                <span className="col-span-2 text-xs text-slate-500">{o.organism_class||'—'}</span>
                <span className="col-span-2 text-xs text-slate-500">{o.morphology||'—'}</span>
                <span className="col-span-1">{o.is_common?<span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">✓ Common</span>:<span className="text-xs text-slate-300">—</span>}</span>
                <span className="col-span-1 text-xs font-mono text-slate-300">{o.snomed_code||'—'}</span>
              </div>
            ))}
          </div>}
          {showForm&&(
            <div className="p-4 bg-blue-50 border-t border-blue-200">
              <h3 className="font-semibold text-blue-800 mb-3">New Organism</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div><label className="label text-xs">Organism Code *</label><Input value={formData.organism_code||''} onChange={e=>setFormData({...formData,organism_code:e.target.value.toUpperCase()})}/></div>
                <div className="col-span-2"><label className="label text-xs">Organism Name *</label><Input value={formData.organism_name||''} onChange={e=>setFormData({...formData,organism_name:e.target.value})}/></div>
                <div><label className="label text-xs">Gram Stain</label><select className="input text-sm" value={formData.gram_stain||''} onChange={e=>setFormData({...formData,gram_stain:e.target.value})}><option value="">Select...</option>{['POSITIVE','NEGATIVE','VARIABLE','ACID_FAST','NOT_APPLICABLE'].map(g=><option key={g}>{g}</option>)}</select></div>
                <div><label className="label text-xs">Morphology</label><Input value={formData.morphology||''} onChange={e=>setFormData({...formData,morphology:e.target.value})} placeholder="Cocci, Bacilli..."/></div>
                <div><label className="label text-xs">Class</label><Input value={formData.organism_class||''} onChange={e=>setFormData({...formData,organism_class:e.target.value})}/></div>
                <div><label className="label text-xs">SNOMED Code</label><Input value={formData.snomed_code||''} onChange={e=>setFormData({...formData,snomed_code:e.target.value})}/></div>
                <label className="flex items-center gap-2 text-sm col-span-2"><input type="checkbox" checked={!!formData.is_common} onChange={e=>setFormData({...formData,is_common:e.target.checked})} className="w-4 h-4 accent-blue-600"/> Mark as Common Organism</label>
              </div>
              <div className="flex gap-2 mt-3">
                <button onClick={saveRecord} disabled={saving} className="btn-primary text-xs">{saving?'Saving...':'Save Organism'}</button>
                <button onClick={()=>setShowForm(false)} className="btn-secondary text-xs">Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ══ ANTIBIOTIC MASTER ══ */}
      {tab==='antibiotics'&&(
        <div className="card overflow-hidden">
          <div className="grid grid-cols-12 p-3 bg-slate-50 border-b text-xs font-semibold text-slate-500 uppercase gap-2">
            <span className="col-span-1">Code</span><span className="col-span-3">Antibiotic Name</span><span className="col-span-1">Short</span><span className="col-span-2">Class</span><span className="col-span-1">Disc</span><span className="col-span-2">WHO</span><span className="col-span-1">Oral</span><span className="col-span-1">IV</span>
          </div>
          {loading?<p className="p-8 text-center text-slate-400">Loading...</p>
          :<div className="divide-y max-h-[60vh] overflow-y-auto">
            {filter(antibiotics,['antibiotic_code','antibiotic_name','antibiotic_class']).map((a:any)=>(
              <div key={a.id} className="grid grid-cols-12 gap-2 p-3 items-center hover:bg-slate-50 text-sm">
                <span className="col-span-1 font-mono text-xs font-bold text-[#02357A]">{a.antibiotic_code}</span>
                <span className="col-span-3 font-medium text-slate-800">{a.antibiotic_name}</span>
                <span className="col-span-1 font-mono text-xs text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">{a.short_name||'—'}</span>
                <span className="col-span-2 text-xs text-slate-500">{a.antibiotic_class||'—'}</span>
                <span className="col-span-1 text-xs font-mono text-slate-400">{a.disc_strength||'—'}</span>
                <span className="col-span-2"><Badge label={a.who_classification||'—'} className={`text-xs ${WHO_COLORS[a.who_classification]||'bg-slate-100 text-slate-500'}`}/></span>
                <span className="col-span-1 text-center text-xs">{a.oral_available?'✅':'—'}</span>
                <span className="col-span-1 text-center text-xs">{a.iv_available?'✅':'—'}</span>
              </div>
            ))}
          </div>}
        </div>
      )}

      {/* ══ UNIT MASTER ══ */}
      {tab==='units'&&(
        <div className="card overflow-hidden">
          <div className="grid grid-cols-12 p-3 bg-slate-50 border-b text-xs font-semibold text-slate-500 uppercase gap-2">
            <span className="col-span-2">Code</span><span className="col-span-4">Full Name</span><span className="col-span-2">Type</span><span className="col-span-2">Base Unit</span><span className="col-span-2">SI Unit</span>
          </div>
          {loading?<p className="p-8 text-center text-slate-400">Loading...</p>
          :<div className="divide-y max-h-[60vh] overflow-y-auto">
            {filter(units,['unit_code','unit_name','unit_type']).map((u:any)=>(
              <div key={u.id} className="grid grid-cols-12 gap-2 p-3 items-center hover:bg-slate-50 text-sm">
                <span className="col-span-2 font-mono text-xs font-bold text-[#02357A]">{u.unit_code}</span>
                <span className="col-span-4 text-slate-700">{u.unit_name}</span>
                <span className="col-span-2 text-xs text-slate-500">{u.unit_type||'—'}</span>
                <span className="col-span-2 text-xs font-mono text-slate-400">{u.base_unit_code||'—'}</span>
                <span className="col-span-2">{u.si_unit?<Badge label="SI" className="bg-blue-100 text-[#02357A] text-xs"/>:<span className="text-xs text-slate-300">—</span>}</span>
              </div>
            ))}
          </div>}
        </div>
      )}

      {/* ══ FORMULA MASTER ══ */}
      {tab==='formulas'&&(
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {loading?<p className="col-span-2 text-center text-slate-400 py-8">Loading...</p>
          :filter(formulas,['formula_code','formula_name','formula_expression','output_parameter_code']).map((f:any)=>(
            <div key={f.id} className="card p-4 hover:shadow transition-shadow">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <p className="font-semibold text-slate-800 text-sm">{f.formula_name}</p>
                  <p className="text-xs font-mono text-[#02357A]">{f.formula_code}</p>
                </div>
                <Badge label={f.output_parameter_code||'—'} className="bg-indigo-100 text-indigo-700 text-xs font-mono"/>
              </div>
              <div className="bg-slate-900 text-green-400 font-mono text-sm px-3 py-2 rounded-lg mb-2">
                {f.output_parameter_code} = {f.formula_expression}
              </div>
              {f.input_parameters&&(
                <div className="flex flex-wrap gap-1">
                  <span className="text-xs text-slate-400">Inputs:</span>
                  {(Array.isArray(f.input_parameters)?f.input_parameters:JSON.parse(f.input_parameters||'[]')).map((p:string)=>(
                    <span key={p} className="text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded font-mono">{p}</span>
                  ))}
                </div>
              )}
              {f.description&&<p className="text-xs text-slate-400 mt-2 italic">{f.description}</p>}
            </div>
          ))}
        </div>
      )}

      {/* ══ INTERPRETATION RULES ══ */}
      {tab==='rules'&&(
        <div className="card overflow-hidden">
          <div className="grid grid-cols-12 p-3 bg-slate-50 border-b text-xs font-semibold text-slate-500 uppercase gap-2">
            <span className="col-span-1">Param</span><span className="col-span-2">Condition</span><span className="col-span-1">Severity</span><span className="col-span-7">Interpretation Text</span><span className="col-span-1">Priority</span>
          </div>
          {loading?<p className="p-8 text-center text-slate-400">Loading...</p>
          :<div className="divide-y max-h-[55vh] overflow-y-auto">
            {filter(rules,['rule_code','parameter_code','interpretation_text']).map((r:any)=>(
              <div key={r.id} className="grid grid-cols-12 gap-2 p-3 items-start hover:bg-slate-50 text-sm">
                <span className="col-span-1 font-mono text-xs font-bold text-[#02357A]">{r.parameter_code}</span>
                <div className="col-span-2 text-xs text-slate-500">
                  <span>{r.condition_type?.replace(/_/g,' ')}</span>
                  {r.condition_value_high&&<span className="ml-1 font-mono">{r.condition_value_high}</span>}
                  {r.condition_value_low&&r.condition_value_high&&<span className="font-mono">–{r.condition_value_high}</span>}
                </div>
                <span className="col-span-1"><Badge label={r.severity||'NORMAL'} className={`text-xs ${SEV_COLORS[r.severity]||'bg-slate-100'}`}/></span>
                <span className="col-span-7 text-xs text-slate-700">{r.interpretation_text}</span>
                <span className="col-span-1 text-xs text-slate-400 text-center">{r.priority||0}</span>
              </div>
            ))}
          </div>}
          {showForm&&(
            <div className="p-4 bg-blue-50 border-t border-blue-200">
              <h3 className="font-semibold text-blue-800 mb-3">New Interpretation Rule</h3>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <div><label className="label text-xs">Rule Code *</label><Input value={formData.rule_code||''} onChange={e=>setFormData({...formData,rule_code:e.target.value.toUpperCase()})}/></div>
                <div><label className="label text-xs">Parameter Code *</label><Input value={formData.parameter_code||''} onChange={e=>setFormData({...formData,parameter_code:e.target.value.toUpperCase()})}/></div>
                <div><label className="label text-xs">Condition Type</label><select className="input text-sm" value={formData.condition_type||''} onChange={e=>setFormData({...formData,condition_type:e.target.value})}><option value="">Select...</option>{['LESS_THAN','GREATER_THAN','BETWEEN','EQUALS'].map(c=><option key={c}>{c}</option>)}</select></div>
                <div><label className="label text-xs">Severity</label><select className="input text-sm" value={formData.severity||'NORMAL'} onChange={e=>setFormData({...formData,severity:e.target.value})}>{['NORMAL','MILD','MODERATE','SEVERE','CRITICAL'].map(s=><option key={s}>{s}</option>)}</select></div>
                {(formData.condition_type==='LESS_THAN'||formData.condition_type==='GREATER_THAN')&&<div><label className="label text-xs">Threshold Value</label><Input type="number" value={formData.condition_type==='LESS_THAN'?formData.condition_value_high||'':formData.condition_value_low||''} onChange={e=>formData.condition_type==='LESS_THAN'?setFormData({...formData,condition_value_high:+e.target.value}):setFormData({...formData,condition_value_low:+e.target.value})}/></div>}
                {formData.condition_type==='BETWEEN'&&<><div><label className="label text-xs">Min Value</label><Input type="number" value={formData.condition_value_low||''} onChange={e=>setFormData({...formData,condition_value_low:+e.target.value})}/></div><div><label className="label text-xs">Max Value</label><Input type="number" value={formData.condition_value_high||''} onChange={e=>setFormData({...formData,condition_value_high:+e.target.value})}/></div></>}
                <div className="col-span-4"><label className="label text-xs">Interpretation Text *</label><textarea className="input w-full text-sm" rows={2} value={formData.interpretation_text||''} onChange={e=>setFormData({...formData,interpretation_text:e.target.value})}/></div>
              </div>
              <div className="flex gap-2 mt-3">
                <button onClick={saveRecord} disabled={saving} className="btn-primary text-xs">{saving?'Saving...':'Save Rule'}</button>
                <button onClick={()=>setShowForm(false)} className="btn-secondary text-xs">Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}
    </Layout>
  );
}
